import React, { useState } from "react";
import axios from "axios";

const ReviewForm = ({ providerId, userData, onReviewSubmitted }) => {
  const [formData, setFormData] = useState({
    title: "",
    comment: "",
    rating: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating: rating
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Validate userData
    if (!userData || !userData.userid) {
      setError("User information not available. Please log in again.");
      setIsSubmitting(false);
      return;
    }


    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/review`,
        {
          userId: userData.userid,
          providerId: providerId,
          title: formData.title,
          comment: formData.comment,
          rating: formData.rating,
        }
      );

      if (response.status === 201) {
        setSuccess(true);
        setFormData({ title: "", comment: "", rating: 0 });
        if (onReviewSubmitted) {
          onReviewSubmitted();
        }
        // Reset success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      console.error("Failed to submit review:", err);
      setError(err.response?.data?.message || "Failed to submit review. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="bg-green-900/20 border border-green-500 rounded-lg p-4 mb-4">
        <div className="flex items-center">
          <div className="text-green-400 text-xl mr-2">✓</div>
          <div>
            <h3 className="text-green-400 font-semibold">Review Submitted!</h3>
            <p className="text-gray-300 text-sm">Thank you for your review.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#2F2F2F] p-4 rounded-lg mb-4">
      <h3 className="text-lg font-semibold mb-4">Write a Review</h3>
      
      {error && (
        <div className="bg-red-900/20 border border-red-500 rounded-lg p-3 mb-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Rating */}
        <div>
          <label className="block text-sm font-medium mb-2">Rating *</label>
          <div className="flex space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => handleRatingChange(star)}
                className={`text-2xl ${
                  star <= formData.rating
                    ? "text-yellow-400"
                    : "text-gray-400 hover:text-yellow-300"
                } transition-colors`}
              >
                ★
              </button>
            ))}
          </div>
          {formData.rating > 0 && (
            <p className="text-sm text-gray-400 mt-1">
              {formData.rating} star{formData.rating !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium mb-2">
            Title (optional)
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Give your review a title..."
            maxLength={100}
          />
        </div>

        {/* Comment */}
        <div>
          <label htmlFor="comment" className="block text-sm font-medium mb-2">
            Comment (optional)
          </label>
          <textarea
            id="comment"
            name="comment"
            value={formData.comment}
            onChange={handleInputChange}
            rows={4}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            placeholder="Share your experience with this provider..."
            maxLength={500}
          />
          <p className="text-xs text-gray-400 mt-1">
            {formData.comment.length}/500 characters
          </p>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting || formData.rating === 0}
          className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
            isSubmitting || formData.rating === 0
              ? "bg-gray-600 text-gray-400 cursor-not-allowed"
              : "bg-purple-600 hover:bg-purple-700 text-white"
          }`}
        >
          {isSubmitting ? "Submitting..." : "Submit Review"}
        </button>
      </form>
    </div>
  );
};

export default ReviewForm;
